// useEventHandler.ts
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ArenaBattleStateEnum, BattleUpdateType, ConnectionStatus, MessagesVariant, SignalREvent, TroopActions, TroopAttribute, ViewState } from '../types/enum';
import { BattlePlayerInfo, BattleRecord, BattleResult, BattleRules, EventHandlers, GameInfo, PlayerInfo, SuggestedMatchData, TroopAction, TroopActionTarget, Unit } from '../types';
import useGameLogic from './useGameLogic';
import { useBattleContext } from '../context';
import prepareTroops from '../utils/prepare-troops';
import { useDispatch, useSelector } from 'react-redux';
import usePing from './usePing';
import { useLocation, useNavigate } from 'react-router-dom';
import useTriggerEvent from './useTriggerEvent';
import { EventNames } from '../utils/event-bus';
import useToast from '../../hooks/useToast';
import useSounds from '../../hooks/useSounds';
import { IsBeta } from '../../utils/Network';
import { normalizeModifierRules } from '../../utils/statModifiers';
import { CloseArenaDialog } from '../../redux/arena-dialog/action';
import { useTour } from '../../components/Tour/TourProvider';
import { useAppTranslation } from '../../i18n/useAppTranslation';


const useEventHandler = () => {
    const triggerEvent = useTriggerEvent()
    const {
      restartTour,
      completeTour,
      startTour,
      skipTour,
      isTourActive,
      isFirstVisit, 
    } = useTour();
    const { timer, pong } = usePing();
    const { } = useGameLogic()
    const navigate = useNavigate()
    const location = useLocation()
    const toast = useToast();
    const { t } = useAppTranslation("battle");
    const dispatch = useDispatch()
    const userInfo = useSelector((state: any) => state.userInfo);
    const { messages, setMessages,
        setSuggestedMatch,
        setCoolDowns, connectionStatus, setConnectionStatus,
        battleState, setBattleState, setGameInfo, isFindingMatch, setIsFindingMatch } = useBattleContext();
    const battleStateRef = React.useRef(battleState);
    const pathnameRef = React.useRef(location.pathname)
    const { playSound } = useSounds();
      const isActiveRef = useRef(isTourActive);
      useEffect(()=>{
        isActiveRef.current=isTourActive
      },[isTourActive])

    useEffect(() => {
        if (pathnameRef.current !== location.pathname) {
            pathnameRef.current = location.pathname
        }
    }, [location.pathname])

  useEffect(() => {
    battleStateRef.current = battleState;
  }, [battleState]);

    const fetchTroops = async () => {
        try {
            const response = await fetch(
                `https://${IsBeta?"devbattle":"battle"}.chainoflegends.com/api/battle/troops`
            );
            // const response = await fetch('https://localhost:7291/api/battle/troops');
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const data = await response.json();
            var storedArmy = JSON.parse(localStorage.getItem("selectedArmy") || "[]");
            setBattleState((prevState) => ({
                ...prevState,
                selectedArmy: storedArmy,
                troops: data,
            })); // Update state with the fetched data
        } catch (error) {
            console.error("Error fetching troops:", error);
        }
    };

    // Event stat modifiers running now, to mark the troops and items they change in the formation.
    // A battle server without the list answers 404: nothing is marked.
    const fetchStatModifierRules = async () => {
        try {
            const response = await fetch(
                `https://${IsBeta?"devbattle":"battle"}.chainoflegends.com/api/battle/statModifiers`
            );
            if (!response.ok) return;
            const rules = normalizeModifierRules(await response.json());
            setBattleState((prevState) => ({ ...prevState, statModifierRules: rules }));
        } catch (error) {
            console.error("Error fetching stat modifiers:", error);
        }
    };

  useEffect(() => {
    fetchTroops();
    fetchStatModifierRules();
  }, []);

    const handlePlayerState = (playerState: ArenaBattleStateEnum) => {
        if (!playerState) return
        switch (playerState) {
            case ArenaBattleStateEnum.Free:
                {
                }
                break;
            case ArenaBattleStateEnum.FindingMatch:
                break;
            case ArenaBattleStateEnum.HostingMatch:
                {
                    const target = '/arena/host-match'
                    // TODO: Fix this
                    // if (pathnameRef.current !== target) navigate(target)
                }
                break;
            case ArenaBattleStateEnum.ReadyCheck:
                break;
            case ArenaBattleStateEnum.ReadyCheckSubmitted:
                break;
            case ArenaBattleStateEnum.Formation:
                {
                    const target = '/arena/formation'
                    if (pathnameRef.current !== target) navigate(target)
                }
                break;
            case ArenaBattleStateEnum.FormationSubmitted:
                {
                    const target = '/arena/formation'
                    if (pathnameRef.current !== target) navigate(target)
                }
                break;
            case ArenaBattleStateEnum.InBattle:
                {
                    const target = '/arena/live'
                    if (pathnameRef.current !== target)  navigate('/arena/live') 
                }
                break;
            default:
                break;
        }
    }
    const onPong = (pingValue: number) => {
        triggerEvent(EventNames.Ping, pingValue)
        pong()
        // if (connectionStatus !== ConnectionStatus.Connected) setConnectionStatus(ConnectionStatus.Connected)

    };

  const messageHandler = useCallback((message: string, type: number) => {
    // TODO: Add message handler
    const variant =
      type === 3 ? MessagesVariant.Error : MessagesVariant.Success;
    setMessages((prevState) => [
      ...prevState,
      {
        message,
        variant,
      },
    ]);
  }, []);

  const updateUnitPosition = (unit: Unit, action: TroopAction) => {
    setCoolDowns((prevState) => ({
      ...prevState,
      troops: {
        ...prevState.troops,
        [unit.playerTroopId]: {
          ...prevState.troops[unit.playerTroopId],
          moveCoolDownDate:
            Date.now() + battleStateRef.current.battleRules?.moveCoolDown!,
        },
      },
    }));
    return {
      ...unit,
      position: { x: action.targetX, y: action.targetY },
      x: action.targetX,
      y: action.targetY,
      moveKey: action.actionType <= 2 ? (unit.moveKey || 0) + 1 : unit.moveKey,
      attackKey:
        action.actionType === 3 ? (unit.attackKey || 0) + 1 : unit.attackKey,
      healKey: action.actionType === 4 ? (unit.healKey || 0) + 1 : unit.healKey,
    };
  };

  const updateUnitAttributes = (
    unit: Unit,
    targets: TroopActionTarget[],
    playerTroopId: number
  ) => {
    let updatedUnit = { ...unit };
    targets.forEach((target) => {
      switch (target.targetAttribute) {
        case TroopAttribute.Health: {
          // Health

          const hpChange = target.newValue - updatedUnit.health;
          if (hpChange > 0) {
            // TODO: add healing config
            setCoolDowns((prevState) => ({
              ...prevState,
              troops: {
                ...prevState.troops,
                [playerTroopId]: {
                  ...prevState.troops[playerTroopId],
                  healingCoolDownDate:
                    Date.now() +
                    battleStateRef.current.battleRules?.attackCooldown!,
                },
              },
            }));
          }

          if (hpChange < 0) {
            setCoolDowns((prevState) => ({
              ...prevState,
              troops: {
                ...prevState.troops,
                [playerTroopId]: {
                  ...prevState.troops[playerTroopId],
                  actionCoolDownDate:
                    Date.now() +
                    battleStateRef.current.battleRules?.attackCooldown!,
                },
              },
            }));
          }
          if (hpChange !== 0) {
            triggerEvent(EventNames.StartAttack, {
              modelId: playerTroopId + "",
            });
            triggerEvent(EventNames.Damage, {
              modelId: target.id + "",
              damage: hpChange,
            });

            updatedUnit = {
              ...updatedUnit,
              healthChanges: [
                ...(updatedUnit.healthChanges || []).filter(
                  (h) => Date.now() - h.id < 1500
                ),
                { id: Date.now(), value: hpChange },
              ],
              health: target.newValue,
            };
          }
          break;
        }
        case TroopAttribute.Armor: // Armor
          // console.log(
          //     `${updatedUnit.troop.name} level ${updatedUnit.level} armor updated from ${updatedUnit.armor} to ${target.newValue}`
          // );
          updatedUnit.armor = target.newValue;
          break;
        case TroopAttribute.MagicResistance: // Magic Resistance
          // console.log(
          //     `${updatedUnit.troop.name} level ${updatedUnit.level} magic resist updated from ${updatedUnit.magicResistance} to ${target.newValue}`
          // );
          updatedUnit.magicResistance = target.newValue;
          break;
        case TroopAttribute.Damage: // Damage
          updatedUnit.damage = target.newValue;
          break;
        case TroopAttribute.Range: // Range
          updatedUnit.range = target.newValue;
          break;
        case TroopAttribute.Buffs: // Buffs
          updatedUnit.buffs = target.newValue;
          break;
        default:
          break;
      }
    });
    return updatedUnit;
  };

  const updateUnits = (
    units: Unit[],
    action: TroopAction,
    updateType: number
  ) => {
    if (updateType !== 5) return units;
    // console.log("updateUnits", units, action, updateType);
    return units.map((unit) =>
      unit.playerTroopId === action.playerTroopId
        ? updateUnitPosition(unit, action)
        : unit
    );
  };

  const applyActionTargets = (units: Unit[], action: TroopAction) => {
    if (!action) return units;
    return units.map((unit) => {
      const targets = action.targets?.filter(
        (t) => t && t.id === unit.playerTroopId
      );
      return targets && targets.length > 0
        ? updateUnitAttributes(unit, targets, action.playerTroopId)
        : unit;
    });
  };


    const eventUpdateHandler = useCallback((message: string, updateType: BattleUpdateType, action: TroopAction) => {
        // console.log("new battle update: ", message, updateType, action);

        if (IsBeta) return

        setBattleState((prevState) => {
            const actionedUnit = prevState.units.find(
                (u) => u.playerTroopId === action?.playerTroopId
            );
            let newUnits = updateUnits(prevState.units, action, updateType);
            newUnits = applyActionTargets(newUnits, action);
            const resetAddTroopCooldown =
                actionedUnit &&
                action &&
                actionedUnit.playerId === userInfo.id &&
                action.actionType === TroopActions.Enter;
            if (resetAddTroopCooldown) setCoolDowns((prevState) => ({
                ...prevState,
                troopCoolDownDate: Date.now() + battleStateRef.current.battleRules?.troopAddCooldown!,
            }))

        return {
          ...prevState,
          addCooldown: prevState.addCooldown + (resetAddTroopCooldown ? 1 : 0),
          units: newUnits,
        };
      });
    },
    []
  );

    const onStartBattle = (_battleId: string, defenders: any, attackers: any, hostPlayer: PlayerInfo[], opponentPlayer: PlayerInfo[], battleRules: BattleRules, startTime: string, isWatcher: boolean) => {
        if (battleStateRef.current.troops.length == 0) {
            setTimeout(() => {
                onStartBattle(_battleId, attackers, defenders, hostPlayer, opponentPlayer, battleRules, startTime, isWatcher);
            }, 1000);
            return;
        }
        attackers = attackers || [];
        defenders = defenders || [];
        const playerIsAttacker =
            attackers.find((a) => a.playerId === userInfo.id) != null;
        attackers = prepareTroops(attackers, playerIsAttacker, true, battleStateRef);
        defenders = prepareTroops(defenders, playerIsAttacker, false, battleStateRef);
        setBattleState((prevState) => ({
            ...prevState,
            battleRules,
            players: [hostPlayer, opponentPlayer],
            viewState: ViewState.LiveBattle,
            playerIsAttacker: playerIsAttacker,
            units: attackers.concat(defenders),
            battleId: _battleId,
            isWatcher
        }));
        navigate('/arena/live');
        // setViewState(ViewState.LiveBattle);
    };

  const clearSteps = () => {
    setIsFindingMatch(false);
    setSuggestedMatch(undefined);
  };

    const onFormationCancel = () => {
        setTimeout(() => {
            navigate('/home')
            toast({
                ok: false,
                message: t("notifications.formationCanceled")
            })
        }, 2000);
    }

    const eventHandlers: EventHandlers = useMemo(() => ({
        onMessageReceived: (username: string, message: string) => {
            // console.log('Message received:', username, message);
        },
        onBattleUpdate: (message: string, updateType: BattleUpdateType, action) => {
            // console.log('Battle update:', message, updateType, action);
            if (updateType === BattleUpdateType.FindMatchCanceled) {
                clearSteps()
            }
            if (updateType === BattleUpdateType.ReadyCheckCanceled) {
                toast({
                    ok: false,
                    message: t("notifications.readyCheckCanceled")
                })
                navigate("/home")
            }
            // if (updateType === BattleUpdateType.AFKTimeout || updateType === BattleUpdateType.BattleTimeout || updateType === BattleUpdateType.EndBattle) {
            //     triggerEvent(EventNames.BattleEnd, null);
            // }
            if (updateType === BattleUpdateType.FormationCanceled) onFormationCancel();
            if (message) return messageHandler(message, updateType);
            eventUpdateHandler(message, updateType, action);
        },
        onReadyCheck: (data: SuggestedMatchData) => {
            dispatch(CloseArenaDialog())
            setIsFindingMatch(false);
            setSuggestedMatch(data)
            navigate('/arena/battle-lobby')
            playSound('notice')
            // TODO : Get Battle Data and Store it
        },
        onStartBattle: (battleId: string, attackers: any, defenders: any, hostPlayer: PlayerInfo[], opponentPlayer: PlayerInfo[], battleRules: BattleRules, startTime: string, isWatcher: boolean) => {
            // console.log('Battle Started:', battleId, attackers, defenders);
            onStartBattle(battleId, attackers, defenders, hostPlayer, opponentPlayer, battleRules, startTime, isWatcher);
            clearSteps();
        },
        onPong,
        onBattleList: (liveBattles: BattleRecord[]) => {
            triggerEvent(EventNames.BattleList, liveBattles)
        },
        onNewBattle: (battleId: string, attackerNames, defenderNames, attackers, defenders) => {
            triggerEvent(EventNames.NewBattle, { battleId, attackerNames, defenderNames, attackers, defenders })
        },
        onEndBattle: (battleId, winnerPlayerIds, battleResult, attackers, defenders) => {
            triggerEvent(EventNames.BattleResult, battleResult)
            triggerEvent(EventNames.EndBattle, { battleId, winnerPlayerIds, battleResult, attackers, defenders })
            if(isActiveRef){
              restartTour("arenaTour")
            }
          },
        onStatusChange: (status: ConnectionStatus, error?: any) => {
            setConnectionStatus(status)
            // console.log('Status Change:', status, error);
        },
        onSignIn: (gameInfo: GameInfo) => {
            // console.log('Sign In: ', gameInfo);
            handlePlayerState(gameInfo.playerInfo?.arenaPlayerState)
            setGameInfo(gameInfo);
        },
        onStartFormation: (battleId, army, battleRules, battleModeInfo) => {
            // Refresh at every match, so a page left open sees an event start or end.
            fetchStatModifierRules();
            // console.log('Start Formation for Battle:', battleId);
            // setViewState(ViewState.TroopSelection);
            setBattleState(prev => ({
                ...prev,
                battleRules
            }))
            navigate('/arena/formation')
            setTimeout(() => {
                triggerEvent(EventNames.StartFormation, { troops: army, rules: battleRules, battleModeInfo: battleModeInfo })
            }, 1000);
        },
        onHostCreated: (battleId: string) => {
            // console.log('Host Created:', battleId);
            navigate('/arena/host-match?id=' + battleId)
        },
    }), []);



  return eventHandlers;
};

export default useEventHandler;
